import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useLoader } from '../../../../HOC/LoaderProvider';
import useAsyncGuiHandler from '../../../../hooks/useAsyncHandler';
import { Booth } from '../../../../interfaces/interfaces';
import { fetchAndSetAdminBooth } from '../../../../reduxToolkit/slices/adminSlice';
import { setPDFModal } from '../../../../reduxToolkit/slices/commonSlice';
import { useDispatch, useSelector } from '../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../services/helper';
import { updateBooth } from '../../../../services/services';
import VyButton from '../../../common/VyButton';
import VyTextField from '../../../common/VyTextField';
import UploadPDF from '../../UploadPDF';
import { MdDelete, MdOpenInNew } from 'react-icons/md';
import VyTooltip from '../../../common/VyTooltip';
import { VyConfirm } from '../../../common/VyConfirm';
import AboutItem from '../../event/Homepage/Overview/HomepageAbout/AboutItem';
import Link from 'next/link';

interface BoothPDFsProps {
  booth: Booth;
  isInline?: boolean;
  onRefresh?: () => void;
}

const BoothPDFs = ({ booth, isInline = false, onRefresh }: BoothPDFsProps) => {
  const { t } = useTranslation();
  const event = useSelector((state) => state.admin.event ?? state.common.event);
  const dispatch = useDispatch();
  const [handleAsync] = useAsyncGuiHandler();
  const { startLoader, stopLoader } = useLoader();

  const [isDeleting, setIsDeleting] = useState(false);

  const pdfUploads = booth?.pdfUploads || [];

  function openPDFModal() {
    dispatch(setPDFModal(true));
  }

  async function handlePDFNameChange(fileid: string, name: string) {
    const updatedPDFUploads = pdfUploads.map((pdf) => {
      if (pdf.file === fileid) return { ...pdf, name };
      return pdf;
    });
    await updateBooth({ boothid: booth._id, pdfUploads: updatedPDFUploads });
    await refresh();
  }

  async function deletePDF(fileid: string) {
    setIsDeleting(true);
    const newPDFUploads = pdfUploads.filter((pdf) => pdf.file !== fileid);
    await updateBooth({ boothid: booth._id, pdfUploads: newPDFUploads });
    await refresh();
    setIsDeleting(false);
  }

  async function refresh() {
    !isInline && dispatch(fetchAndSetAdminBooth({ boothid: booth._id }));
    onRefresh?.();
  }

  async function reorder(fromIndex: number, toIndex: number) {
    const newPdfs = [...pdfUploads];
    const [movingPdf] = newPdfs.splice(fromIndex, 1);
    newPdfs.splice(toIndex, 0, movingPdf);
    startLoader();
    await updateBooth({ boothid: booth._id, pdfUploads: newPdfs });
    await refresh();
    stopLoader();
  }

  const shouldDisplayOpenPDFModal = () => {
    if (booth.hasUnlimitedRooms) {
      return true;
    }
    if (event && event.settings.maxBoothPdfUpload > 0) {
      return booth.maxPdfUploads ? pdfUploads.length < booth.maxPdfUploads : pdfUploads.length < event.settings.maxBoothPdfUpload;
    }
    return false;
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full">
        <div className="flex flex-col">
          {shouldDisplayOpenPDFModal() ? (
            <div className="flex flex-col space-y-1 text-center ">
              <VyButton name="upload-pdf" onClick={openPDFModal}>
                {t('booth:upload-pdf')}
              </VyButton>
              {!booth.hasUnlimitedRooms && (
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {t('booth:pdf-upload-limit', { limit: booth.maxPdfUploads || (event && event.settings.maxBoothPdfUpload) })}
                </Typography>
              )}
            </div>
          ) : (
            <div className="text-center">
              {booth.maxPdfUploads !== 0 && (
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {t('booth:pdf-upload-limit-exceeded')}
                </Typography>
              )}
            </div>
          )}
        </div>
        {pdfUploads.map((item, index) => (
          <div key={item._id} className="flex flex-col w-full my-2 flex-end">
            <div className="flex items-center justify-between w-full h-full space-x-4">
              <AboutItem
                useFullWidth
                title=""
                defaultValue={item.name}
                update={async (value) => handleAsync(handlePDFNameChange(item.file, value?.toString()!))}
                previewElement={<p className="font-bold">{item.name}</p>}
                editElement={(disabled, onChange, defaultValue) => (
                  <VyTextField
                    color="primary"
                    type="text"
                    label="PDF Name"
                    className="w-full"
                    defaultValue={defaultValue?.toString()}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />

              <div className="flex items-center h-full">
                <VyTooltip title={t('booth:show-pdf')}>
                  <Link href={createDisplayLink(item.file)} target="_blank">
                    <IconButton>
                      <MdOpenInNew />
                    </IconButton>
                  </Link>
                </VyTooltip>
                {index !== 0 && (
                  <IconButton onClick={() => reorder(index, index - 1)}>
                    <ExpandLessIcon />
                  </IconButton>
                )}
                {index !== pdfUploads.length - 1 && (
                  <VyTooltip title={t('booth:change-sorting')}>
                    <IconButton onClick={() => reorder(index, index + 1)}>
                      <ExpandMoreIcon />
                    </IconButton>
                  </VyTooltip>
                )}
                <VyConfirm onConfirm={() => handleAsync(deletePDF(item.file))}>
                  <VyTooltip title={t('common:delete')}>
                    <IconButton>
                      <MdDelete />
                    </IconButton>
                  </VyTooltip>
                </VyConfirm>
              </div>
            </div>
          </div>
        ))}
      </div>
      <UploadPDF onRefresh={onRefresh} booth={booth} inline />
    </>
  );
};

export default BoothPDFs;
